import { PW_AUTH_TOKEN_KEY } from "@pathwright/auth/src/api/client"
import { useRef } from "react"

type QueryState<TData> = {
  client: object
  data: TData | undefined
  loading: boolean
  error?: unknown
}

type RetainedQueryDataOptions = {
  /** Extra scope keys, such as resource IDs. Client and auth token are always included. */
  scopeKeys?: readonly unknown[]
  /** Persistent shells may explicitly retain UI across scope changes/errors. */
  retainWhileUnavailable?: boolean
}

/**
 * Keep resolved query data usable during background refreshes within one scope.
 * Unlike Apollo's previousData, retention is gated by successful resolution and
 * the query client, current auth token, and any additional scope keys. The query
 * keeps its own loading/error state and cache/fetch policy.
 */
export default function useRetainedQueryData<TData>(
  { client, data, loading, error }: QueryState<TData>,
  {
    scopeKeys = [],
    retainWhileUnavailable = false
  }: RetainedQueryDataOptions = {}
): TData | undefined {
  const currentScope = [
    client,
    localStorage.getItem(PW_AUTH_TOKEN_KEY),
    ...scopeKeys
  ]
  const retainedData = useRef(data)
  const resolvedScope = useRef<readonly unknown[] | null>(null)
  if (!loading && !error && data !== undefined) {
    retainedData.current = data
    resolvedScope.current = currentScope
  }

  const sameScope =
    resolvedScope.current?.length === currentScope.length &&
    currentScope.every((key, index) =>
      Object.is(key, resolvedScope.current?.[index])
    )

  if (retainWhileUnavailable) return retainedData.current
  if (error || (loading && !sameScope)) return undefined
  return data ?? retainedData.current
}
